import React from 'react'
import PropTypes from 'prop-types'
import { useMount, useSetState } from 'react-use'
import { flowRight } from 'lodash'
import { Trans, useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import { withErrorBoundary } from 'lib/errorBoundary'
import { VERTICAL, wrapVehicle } from 'lib/utils/store/vehicle'
import queryString from 'query-string'
import { deleteLocationSearchParam, getFullPath } from 'lib/location'

import VehiclePriceEstimateForm from 'components/Vehicles/VehiclePriceEstimate/VehiclePriceEstimateForm/VehiclePriceEstimateForm'
// eslint-disable-next-line no-unused-vars
import VehiclePriceEstimateDisplay from 'components/Vehicles/VehiclePriceEstimate/VehiclePriceEstimateDisplay/VehiclePriceEstimateDisplay'
import { VehiclePriceEstimateDisplayNew } from 'components/Vehicles/VehiclePriceEstimate/VehiclePriceEstimateDisplayNew/VehiclePriceEstimateDisplayNew'
import VehicleDataEdit from 'components/Vehicles/VehicleDataEdit/VehicleDataEdit'
import Button from 'components/UI/Button'
import AppContext from 'components/AppContext'

import './vehicle-price-estimate-create.css'

const VehiclePriceEstimateCreate = ({ vehicle, onClose }) => {
    const history = useHistory()
    const location = useLocation()
    const query = queryString.parse(location.search)

    const [state, setState] = useSetState({
        priceEstimateResult: null,
        requiredVehicleDataMissing: !vehicle.verticalData.make || !vehicle.verticalData.model || !vehicle.verticalData.year,
        priceEstimateFailedCount: 0, // allow price estimate to fail once if there is missing data that could be used to enhance results
        priceEstimateFormInitialValues: {
            mileage: query.mileage || '',
        },
        displayCreateEventButton: !query['new-vehicle'],
        eventCreated: false,
        newVehicle: !!query['new-vehicle'],
        displayAddPriceEstimateOnMount: vehicle.hasPriceEstimateEvent,
    })

    useMount(() => {
        // remove new-vehicle param to avoid triggering new event creation from price prediction on page refresh
        if (query['new-vehicle']) {
            const newLocation = deleteLocationSearchParam(location, 'new-vehicle')
            history.replace(getFullPath(newLocation))
        }
    })

    const { t } = useTranslation(['formElements', 'vehicles'])

    const {
        priceEstimateResult,
        priceEstimateFailedCount,
        priceEstimateFormInitialValues,
        displayCreateEventButton,
        eventCreated,
        newVehicle,
        displayAddPriceEstimateOnMount,
    } = state

    const wrappedVehicle = wrapVehicle(vehicle)

    // Check if valid type can have do price estimate
    if (vehicle.vertical !== VERTICAL.CAR && vehicle.vertical !== VERTICAL.BIKE) {
        return (
            <>
                <p>
                    <Trans i18nKey="vehicles:pricePrediction.vehicleTypeNotSupported.message" typeName={vehicle.vertical}>
                        Unfortunately we can&#39;t provide price estimate for vehicle type <b>{vehicle.vertical}</b>.
                    </Trans>
                </p>
                <p>{t('vehicles:pricePrediction.vehicleTypeNotSupported.info')}</p>
            </>
        )
    }
    const renderPriceEstimateForm = () => {
        return (
            <VehiclePriceEstimateForm
                vehicle={vehicle}
                newVehicle={newVehicle}
                initialValues={priceEstimateFormInitialValues}
                onBeforeSubmit={(values) => {
                    setState({ priceEstimateFormInitialValues: values })
                }}
                onComplete={(result) => {
                    setState({ priceEstimateResult: result })
                }}
                onFailed={() => {
                    setState({ priceEstimateFailedCount: priceEstimateFailedCount + 1 })
                }}
            />
        )
    }

    return (
        <AppContext context="vehiclePriceEstimateCreate">
            <div className="vehicle-price-estimate-create">
                {priceEstimateResult ? (
                    <VehiclePriceEstimateDisplayNew
                        vehicle={vehicle}
                        onClose={onClose}
                        onSuccess={() => {
                            setState({
                                displayCreateEventButton: false,
                                eventCreated: true,
                            })
                        }}
                        displayAddPriceEstimateOnMount={displayAddPriceEstimateOnMount}
                        displayCreateEventButton={displayCreateEventButton}
                        eventCreated={eventCreated}
                        notResponsive
                    />
                ) : (
                    <VehicleDataEdit
                        wrappedVehicle={wrappedVehicle}
                        fieldOptions={wrappedVehicle.getPriceEstimateMandatoryFieldsSchema()}
                    >
                        {({ isValid, renderDataForm, completed }) => {
                            // failed with required data, nothing more to do
                            if ((isValid && priceEstimateFailedCount > 0) || priceEstimateFailedCount > 1) {
                                return (
                                    <div>
                                        <h1 className="react-modal__modal-title">{t('vehicles:pricePrediction.modalTitle')}</h1>
                                        <p>
                                            {t('vehicles:pricePrediction.estimateNotAvailable')}
                                        </p>
                                        <div className="action-wrapper">
                                            {onClose && (
                                                <Button
                                                    trackName="priceEstimateFailedClose"
                                                    className="button"
                                                    onClick={onClose}
                                                >
                                                    {t('common:close')}
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                )
                            }

                            // ask to give more data
                            if (!completed && !isValid && (priceEstimateFailedCount === 1 || !wrappedVehicle.getRegNumber())) {
                                return (
                                    <div>
                                        <p>{t('vehicles:pricePrediction.inputMoreDataDescription')}</p>
                                        {renderDataForm()}
                                    </div>
                                )
                            }

                            // render form to fill initial data (eg. mileage) to submit price prediction
                            // this will auto submit if all required fields (eg. mileage) are available.
                            return renderPriceEstimateForm()
                        }}
                    </VehicleDataEdit>

                )}
            </div>
        </AppContext>
    )
}

VehiclePriceEstimateCreate.propTypes = {
    vehicle: PropTypes.object.isRequired,
    onClose: PropTypes.func,
}

export default flowRight(
    withErrorBoundary,
)(VehiclePriceEstimateCreate)
